<script>
import {useClaimStore} from "@/stores/Claim.js";
import useTools from "@/use/Tools.js";
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import {useAbsenceRequestStore} from "@/stores/AbsenceRequest.js";
import {useDebtStore} from "@/stores/Debt.js";
import {onMounted, ref} from "vue";
import useValidation from "@/use/Validation.js";
import {useRecruitmentRequestStore} from "@/stores/Request/RecruitmentRequest.js";
import {usePaySlipAnnexStore} from "@/stores/PaySlipAnnex.js";
import {useTrainingRequestStore} from "@/stores/Training/TrainingRequest";
import axios from "axios";

export default {
    name: "SetDecision",
    setup() {
        const claimStore = useClaimStore();
        const absenceRequestStore = useAbsenceRequestStore();
        const debtStore = useDebtStore();
        const recruitmentRequestStore = useRecruitmentRequestStore();
        const paySlipAnnexStore = usePaySlipAnnexStore();
        const trainingRequestStore = useTrainingRequestStore();

        const {$route, $refs} = useTools();
        const {loading, dialog, inputColor, backgroundColor} = useSharedData();
        const {mxRules} = useValidation();
        const {getBack} = useSharedMethod();

        const decision = ref({
            validationOption: 'KEEP',
        });

        const file = ref(null);

        let api = null;
        switch ($route.params.module) {
            case "DEBT":
                api = "debts"
                break;
            case "ABSENCE_REQUEST":
                api = "absenceRequests";
                break;
            case "CLAIM":
                api = "claims";
                break;
            case "ADMIN_REQUEST":
                api = "adminRequests";
                break;
            case "RECRUITMENT_REQUEST":
                api = "recruitmentRequests";
                break;
            case "PAY_SLIP_ANNEX":
                api = "paySlipAnnexes";
                break;
            case "TRAINING_REQUEST":
                api = "trainingRequests";
                break;
        }

        const loadingNextStepOptions = ref(false);
        const nextStepOptions = ref(null);

        function getNextStepOptions() {
            loadingNextStepOptions.value = true;
            let api = `/validationTimelines/getNextStepOptions/${$route.params.id}/${$route.params.module}`;
            axios.get(api).then(response => {
                nextStepOptions.value = response.data;
            }).finally(() => {
                loadingNextStepOptions.value = false;
            });
        }

        function setDecision(validationOption) {
            if ($refs.form.validate()) {
                loading.value = true;
                if (validationOption) {
                    decision.value.validationOption = validationOption;
                }
                const formData = new FormData();
                formData.append("file", file.value);
                formData.append("observation", decision.value.observation);
                formData.append("validationOption", decision.value.validationOption);
                formData.append("delegateUserId", decision.value.delegateUserId);
                axios.put(`/${api}/setDecision/${$route.params.id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }).then(response => {
                    switch ($route.params.module) {
                        case "DEBT":
                            debtStore.updateDebt(response.data);
                            break;
                        case "ABSENCE_REQUEST":
                            absenceRequestStore.updateAbsenceRequest(response.data);
                            break;
                        case "CLAIM":
                        case "ADMIN_REQUEST":
                            claimStore.updateClaim(response.data);
                            break;
                        case "RECRUITMENT_REQUEST":
                            recruitmentRequestStore.updateRecruitmentRequest(response.data);
                            break;
                        case "PAY_SLIP_ANNEX":
                            paySlipAnnexStore.updatePaySlipAnnex(response.data);
                            break;
                        case "TRAINING_REQUEST":
                            trainingRequestStore.updateTrainingRequest(response.data);
                            break;
                    }
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            getNextStepOptions();
        })

        return {
            mxRules,
            loadingNextStepOptions,
            nextStepOptions,
            dialog,
            loading,
            inputColor,
            backgroundColor,
            file,
            decision,
            setDecision,
            getBack,
        }
    }
}
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        max-width="700"
        scrollable
    >
        <v-card>
            <w-toolbar color="teal" :title="$t('common.set_decision')"/>
            <v-card-text>
                <v-container fluid>
                    <w-card-form without-header>
                        <template v-slot:body>
                            <v-form ref="form" lazy-validation>
                                <v-row>
                                    <v-col cols="12">
                                        <strong>{{ `${$t('common.state')}: ${nextStepOptions.currentStep}` }} *</strong>
                                        <v-progress-linear v-if="loadingNextStepOptions" indeterminate></v-progress-linear>
                                        <!-- :disabled="decision.validationOption === 'DELEGATE' || nextStepOptions.originValidator" -->
                                        <v-radio-group
                                            v-model="decision.validationOption"
                                            :disabled="nextStepOptions.originValidator"
                                            :rules="decision.validationOption === 'DELEGATE' || nextStepOptions.originValidator ? [] : [mxRules.required]"
                                            hide-details="auto"
                                        >
                                            <v-radio label="Accepter" value="ACCEPT"/>
                                            <v-radio label="Réfuser" value="REFUSE"/>
                                            <v-radio label="Garder Etat Actuel" value="KEEP"/>
                                            <v-radio label="Confirmer avec" value="DELEGATE"/>
                                        </v-radio-group>
                                    </v-col>
                                    <v-col cols="12" v-if="decision.validationOption === 'DELEGATE'">
                                        <strong>{{ `${$t('module.worker')} *` }}</strong>
                                        <str-user-autocomplete
                                            v-model="decision.delegateUserId"
                                            :validator="false"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="12">
                                        <strong>{{ `${$t('common.comment')} *` }}</strong>
                                        <v-textarea
                                            v-model="decision.observation"
                                            outlined
                                            autofocus
                                            :background-color="backgroundColor"
                                            dense
                                            :color="inputColor"
                                        ></v-textarea>
                                    </v-col>
                                    <v-col cols="12">
                                        <strong>{{ $t('common.attach') }}</strong>
                                        <v-file-input
                                            v-model="file"
                                            prepend-inner-icon="mdi-paperclip"
                                            prepend-icon=""
                                            outlined
                                            dense
                                            :background-color="backgroundColor"
                                            :color="inputColor"
                                            hide-details
                                        ></v-file-input>
                                    </v-col>

                                </v-row>
                            </v-form>
                        </template>
                    </w-card-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close
                    :loading="loading || loadingNextStepOptions"
                    @press="getBack"
                />
                <w-btn-apply
                    :show="nextStepOptions && !nextStepOptions.originValidator"
                    :loading="loading"
                    @press="setDecision(null)"
                />
                <w-btn-apply
                    :show="nextStepOptions && nextStepOptions.originValidator"
                    :loading="loading"
                   @press="setDecision('DELEGATE')"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>

</style>